import { useEffect, useMemo, useState } from "react";
import { getAllTransactions, deleteTransaction } from "./TransactionService";
import { getAllAccounts } from "../accounts/AccountService";
import { getAllCategories } from "../categories/CategoryService";
import { getAllMerchants } from "../merchants/MerchantService";
import "./TransactionList.css";
import {
  formatCurrency,
  formatTransactionTypeLabel,
  normalizeTransactionType,
} from "./transactionUtils";

const DEFAULT_FILTERS = {
  search: "",
  type: "ALL",
  accountId: "ALL",
  categoryId: "ALL",
  merchantId: "ALL",
  dateFrom: "",
  dateTo: "",
  amountMin: "",
  amountMax: "",
  sortBy: "date_desc",
};

export default function TransactionList({ onEditTransaction, onSplitTransaction, refreshSignal }) {
  const [txns, setTxns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    load();
  }, [refreshSignal]);

  const load = async () => {
    const [transactionsData, accountsData, categoriesData, merchantsData] = await Promise.all([
      getAllTransactions(),
      getAllAccounts(),
      getAllCategories(),
      getAllMerchants(),
    ]);
    setTxns(Array.isArray(transactionsData) ? transactionsData : []);
    setAccounts(Array.isArray(accountsData) ? accountsData : []);
    setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    setMerchants(Array.isArray(merchantsData) ? merchantsData : []);
  };

  const accountById = useMemo(
    () => Object.fromEntries(accounts.map((account) => [account.id, account.accountName || "Account"])),
    [accounts]
  );
  const categoryById = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category.categoryName || "Uncategorized"])),
    [categories]
  );
  const merchantById = useMemo(
    () => Object.fromEntries(merchants.map((merchant) => [merchant.id, merchant.merchantName || "Merchant"])),
    [merchants]
  );

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete transaction?")) return;
    const res = await deleteTransaction(id);
    if (res && res.ok === false) {
      alert("Delete failed. Please try again.");
      return;
    }
    load();
  };

  const filteredTransactions = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const min = Number(filters.amountMin || 0);
    const max = filters.amountMax ? Number(filters.amountMax) : null;

    return txns
      .filter((transaction) => {
        const normalizedType = normalizeTransactionType(transaction.transactionType);
        if (filters.type !== "ALL" && normalizedType !== filters.type) return false;
        if (filters.accountId !== "ALL") {
          const matchesAccount =
            transaction.accountId === filters.accountId ||
            transaction.fromAccountId === filters.accountId ||
            transaction.toAccountId === filters.accountId;
          if (!matchesAccount) return false;
        }
        if (filters.categoryId !== "ALL" && transaction.categoryId !== filters.categoryId) return false;
        if (filters.merchantId !== "ALL" && transaction.merchantId !== filters.merchantId) return false;
        if (filters.dateFrom && String(transaction.transactionDate || "") < filters.dateFrom) return false;
        if (filters.dateTo && String(transaction.transactionDate || "") > filters.dateTo) return false;

        const amount = Number(transaction.transactionAmount || 0);
        if (filters.amountMin && amount < min) return false;
        if (max !== null && !Number.isNaN(max) && amount > max) return false;

        if (!search) return true;
        const haystack = [
          transaction.description,
          transaction.transactionDate,
          transaction.transactionAmount,
          formatTransactionTypeLabel(transaction.transactionType),
          merchantById[transaction.merchantId],
          categoryById[transaction.categoryId],
          accountById[transaction.accountId],
          accountById[transaction.fromAccountId],
          accountById[transaction.toAccountId],
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search);
      })
      .sort((left, right) => sortTransactions(left, right, filters.sortBy, { accountById, categoryById, merchantById }));
  }, [accountById, categoryById, filters, merchantById, txns]);

  const totals = useMemo(() => {
    return filteredTransactions.reduce(
      (summary, transaction) => {
        const amount = Number(transaction.transactionAmount || 0);
        const type = normalizeTransactionType(transaction.transactionType);
        if (type === "INCOME") summary.income += amount;
        if (type === "EXPENSE") summary.expense += amount;
        if (type === "TRANSFER") summary.transfer += amount;
        summary.count += 1;
        return summary;
      },
      { count: 0, income: 0, expense: 0, transfer: 0 }
    );
  }, [filteredTransactions]);

  return (
    <div className="transaction-list-wrap">
      <div className="transaction-list-header">
        <div>
          <h2 className="transaction-list-title">Transactions</h2>
          <div className="transaction-list-subtitle">Search, filter, sort, edit, and split entries from one place.</div>
        </div>
        <button type="button" className="transaction-list-reset-btn" onClick={resetFilters}>
          Reset filters
        </button>
      </div>

      <div className="transaction-list-summary">
        <MetricCard label="Visible transactions" value={String(totals.count)} />
        <MetricCard label="Expense total" value={formatCurrency(totals.expense)} tone="expense" />
        <MetricCard label="Income total" value={formatCurrency(totals.income)} tone="income" />
        <MetricCard label="Transfer total" value={formatCurrency(totals.transfer)} tone="transfer" />
      </div>

      <div className="transaction-list-filters">
        <input
          className="input"
          placeholder="Search description, merchant, category, account..."
          value={filters.search}
          onChange={(e) => updateFilter("search", e.target.value)}
        />

        <select className="input" value={filters.type} onChange={(e) => updateFilter("type", e.target.value)}>
          <option value="ALL">All types</option>
          <option value="EXPENSE">Expenses</option>
          <option value="INCOME">Income</option>
          <option value="TRANSFER">Transfers</option>
        </select>

        <select className="input" value={filters.accountId} onChange={(e) => updateFilter("accountId", e.target.value)}>
          <option value="ALL">All accounts</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.accountName}
            </option>
          ))}
        </select>

        <select className="input" value={filters.categoryId} onChange={(e) => updateFilter("categoryId", e.target.value)}>
          <option value="ALL">All categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.categoryName}
            </option>
          ))}
        </select>

        <select className="input" value={filters.merchantId} onChange={(e) => updateFilter("merchantId", e.target.value)}>
          <option value="ALL">All merchants</option>
          {merchants.map((merchant) => (
            <option key={merchant.id} value={merchant.id}>
              {merchant.merchantName}
            </option>
          ))}
        </select>

        <input className="input" type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} />
        <input className="input" type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} />
        <input
          className="input"
          type="number"
          min="0"
          step="0.01"
          placeholder="Min amount"
          value={filters.amountMin}
          onChange={(e) => updateFilter("amountMin", e.target.value)}
        />
        <input
          className="input"
          type="number"
          min="0"
          step="0.01"
          placeholder="Max amount"
          value={filters.amountMax}
          onChange={(e) => updateFilter("amountMax", e.target.value)}
        />
        <select className="input" value={filters.sortBy} onChange={(e) => updateFilter("sortBy", e.target.value)}>
          <option value="date_desc">Newest first</option>
          <option value="date_asc">Oldest first</option>
          <option value="amount_desc">Amount high to low</option>
          <option value="amount_asc">Amount low to high</option>
          <option value="merchant_asc">Merchant A-Z</option>
          <option value="category_asc">Category A-Z</option>
        </select>
      </div>

      <div className="transaction-list-list">
        {filteredTransactions.length === 0 ? (
          <div className="transaction-list-empty">No transactions match the current filters.</div>
        ) : (
          filteredTransactions.map((transaction) => {
            const normalizedType = normalizeTransactionType(transaction.transactionType);
            const merchantLabel = transaction.merchantId ? merchantById[transaction.merchantId] || "Merchant" : "Transfer";
            const accountLabel = transaction.accountId
              ? accountById[transaction.accountId] || "Account"
              : `${accountById[transaction.fromAccountId] || "From"} → ${accountById[transaction.toAccountId] || "To"}`;

            return (
              <div key={transaction.id} className="transaction-list-card">
                <div className="transaction-list-left">
                  <div className={`transaction-list-badge transaction-list-badge-${normalizedType.toLowerCase()}`}>
                    {formatTransactionTypeLabel(normalizedType)}
                  </div>

                  <div className="transaction-list-main-info">
                    <div className={`transaction-list-amount transaction-list-amount-${normalizedType.toLowerCase()}`}>
                      {formatCurrency(transaction.transactionAmount)}
                    </div>

                    <div className="transaction-list-meta">
                      {merchantLabel} • {transaction.transactionDate}
                    </div>

                    <div className="transaction-list-sub">
                      {transaction.categoryId ? categoryById[transaction.categoryId] || "Uncategorized" : "—"} • {accountLabel}
                    </div>

                    {transaction.description && <div className="transaction-list-desc">{transaction.description}</div>}
                  </div>
                </div>

                <div className="transaction-list-actions">
                  <button type="button" className="transaction-list-action-btn" onClick={() => onEditTransaction?.(transaction)}>
                    Edit
                  </button>
                  {normalizedType !== "TRANSFER" && (
                    <button type="button" className="transaction-list-action-btn" onClick={() => onSplitTransaction?.(transaction)}>
                      Split
                    </button>
                  )}
                  <button onClick={() => handleDelete(transaction.id)} className="transaction-list-del-btn">
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone = "default" }) {
  return (
    <div className={`transaction-list-metric transaction-list-metric-${tone}`}>
      <div className="transaction-list-metric-label">{label}</div>
      <div className="transaction-list-metric-value">{value}</div>
    </div>
  );
}

function sortTransactions(left, right, sortBy, lookup) {
  if (sortBy === "date_asc") {
    return String(left.transactionDate || "").localeCompare(String(right.transactionDate || ""));
  }
  if (sortBy === "amount_desc") {
    return Number(right.transactionAmount || 0) - Number(left.transactionAmount || 0);
  }
  if (sortBy === "amount_asc") {
    return Number(left.transactionAmount || 0) - Number(right.transactionAmount || 0);
  }
  if (sortBy === "merchant_asc") {
    return String(lookup.merchantById[left.merchantId] || "Transfer").localeCompare(
      String(lookup.merchantById[right.merchantId] || "Transfer")
    );
  }
  if (sortBy === "category_asc") {
    return String(lookup.categoryById[left.categoryId] || "Uncategorized").localeCompare(
      String(lookup.categoryById[right.categoryId] || "Uncategorized")
    );
  }
  return String(right.transactionDate || "").localeCompare(String(left.transactionDate || ""));
}
